import { Box, Button, Flex } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PitchRecap } from 'components/common/pitch-recap';
import useContTrainingShotListener from 'components/common/plate-view/useContTrainingShotListener';
import useOnDemandShotListener from 'components/common/plate-view/useOnDemandShotListener';
import { NoDataPlaceholder } from 'components/common/table/no-data-placeholder';
import { CommonTabs } from 'components/common/tabs';
import { PresetTrainingDialogHoC } from 'components/machine/dialogs/preset-training';
import { PitchListDialogs } from 'components/sections/pitch-list/dialogs';
import { PitchListHittingResults } from 'components/sections/pitch-list/hitting-results';
import { PitchListHittingResultsNew } from 'components/sections/pitch-list/hitting-results/index-new';
import useMixinTmsListener from 'components/sections/pitch-list/hooks/useMixinTmsListener';
import { PitchListBodyHoC } from 'components/sections/pitch-list/list/body';
import { PitchListFooter } from 'components/sections/pitch-list/list/footer';
import { PitchListHeader } from 'components/sections/pitch-list/list/header';
import { PitchListSkeleton } from 'components/sections/pitch-list/list/skeleton';
import { PitchListToolbar } from 'components/sections/pitch-list/list/toolbar';
import { PitchListSidebar } from 'components/sections/pitch-list/sidebar';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { PitchesHeader } from 'components/sections/pitches/header';
import env from 'config';
import { AimingContext, AimingProvider } from 'contexts/aiming.context';
import { FiringContext } from 'contexts/firing/context';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { IgnoreAutoFireMode } from 'enums/firing';
import { ResetPlateMode } from 'enums/machine.enums';
import { PitchListDialog } from 'enums/pitch-lists';
import { FF_NAME_ANALYTICS_2 } from 'enums/session-recap/enums';
import { SettingsSection } from 'enums/settings';
import useSyncRecapToggle from 'hooks/pitch-list/useSyncRecapToggle';
import { useDebugPitchAutoTms } from 'hooks/useDebugPitchAutoTms';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useSyncShotListener from 'hooks/useSyncShotListener';
import useSyncStrikeZoneListener from 'hooks/useSyncStrikeZoneListener';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IVerboseComponent } from 'interfaces/i-components';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { isInRange } from 'lib_ts/classes/math.utilities';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { PLStoreMode } from 'lib_ts/enums/pitch-list.enums';
import {
  BuildPriority,
  DEFAULT_DELIVERY,
  DeliveryType,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { ForUpdate, Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IPitch } from 'lib_ts/interfaces/pitches/i-pitch';
import { first, isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import useMiscStore from 'stores/use-misc-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { useVideosStore } from 'stores/use-videos-store';
import './index.scss';

interface IPitchListHocProps {
  mode: PLStoreMode;
  ready?: boolean;
}

// Could move the PitchListStoreProvider outside of this, like how the Pitch List Context provider is set up
export const PitchListHoC = (props: IPitchListHocProps & IVerboseComponent) => {
  const lists = useListLibraryStore((s) => s.lists);
  const activeList = useListLibraryStore((s) => s.activeList);

  const filteredPitches = usePitchListStore((s) => s.filteredPitches);
  const pitches = usePitchListStore((s) => s.pitches);
  const filters = usePitchListStore((s) => s.filters);
  const metricsFilters = usePitchListStore((s) => s.metricsFilters);

  const loadingTms = useTrainedMsStore((s) => s.loading);

  const { machine } = useContext(MachineContext);

  useSyncRecapToggle();

  useDebugPitchAutoTms({
    machineID: machine.machineID,
    ballType: machine.ball_type,
    pitches: pitches,
    force: !env.production,
  });

  // monitor pitches + filter to recreate filteredPitches and set to store
  useEffect(() => {
    const safeName = filters.name?.trim().toLowerCase();

    const output = pitches
      .filter(
        (p) =>
          isNil(filters.trained) || filters.trained === !!p._mx_tms?._mx_trained
      )
      .filter((p) => isNil(filters.type) || filters.type === p.type)
      .filter(
        (p) =>
          isNil(filters.hand) ||
          filters.hand === BallHelper.getPitcherHand(p.bs.px)
      )
      .filter((p) => !safeName || p.name?.toLowerCase().includes(safeName))
      .filter((p) => {
        if (!metricsFilters) {
          return true;
        }

        const { vnet, wnet, xInches, zInches } = metricsFilters;

        verboseDebug(props, 'applying metricsFilters', metricsFilters);

        if (!isInRange(vnet.value, p.bs.vnet)) {
          return false;
        }

        if (!isInRange(wnet.value, p.bs.wnet)) {
          return false;
        }

        if (
          !isInRange(
            xInches.value,
            p?.breaks?.xInches ? -1 * p.breaks.xInches : undefined
          )
        ) {
          return false;
        }

        if (!isInRange(zInches.value, p.breaks?.zInches)) {
          return false;
        }

        return true;
      });

    verboseDebug(
      props,
      `filtered pitches > ${pitches.length} total > ${output.length} filtered`,
      filters
    );

    usePitchListStore.getState().setFilteredPitches(output);
  }, [pitches, filters, metricsFilters, machine.last_hardware_changed]);

  const activeListID = activeList?._id;

  // wait for the initial load of lists to complete before loading pitches
  const ready = useMemo(() => {
    if (!lists.length) {
      return false;
    }

    if (loadingTms) {
      return false;
    }

    return true;
  }, [lists.length, loadingTms]);

  // don't load pitches until we're sure the active list is set correctly
  useEffect(() => {
    if (!ready) {
      return;
    }

    usePitchListStore.getState().resetSearchCriteria({
      mode: props.mode,
      criteria: {
        parent: {
          _parent_id: activeListID, // * will be ignored if in library mode
        },
        list: {
          name: null,
          folder: null,
          parent: null,
        },
        filters: null,
        metricsFilters: null,
      },
    });
  }, [ready, activeListID]);

  const identifier = useMemo(() => {
    switch (props.mode) {
      case PLStoreMode.Library: {
        return 'LibraryIndex';
      }

      case PLStoreMode.PitchList:
      default: {
        return 'PitchListIndex';
      }
    }
  }, [props.mode]);

  return (
    <AimingProvider identifier="PLHoC" training={false} verbose>
      <CheckedProvider data={filteredPitches}>
        <PitchListIndex
          identifier={identifier}
          mode={props.mode}
          verbose={props.verbose}
          // don't show pitches until we're sure the active list is set correctly
          ready={ready}
        />
      </CheckedProvider>
    </AimingProvider>
  );
};

const PitchListIndex = (props: IPitchListHocProps & IVerboseComponent) => {
  useMixinTmsListener();

  useSyncShotListener({
    identifier: 'PLSyncShotListener',
    verbose: false,
  });

  useSyncStrikeZoneListener({
    identifier: 'PLSyncStrikeZoneListener',
    verbose: false,
  });

  useContTrainingShotListener();

  useOnDemandShotListener({
    identifier: 'PLOnDemandShotListener',
    verbose: false,
  });

  const { resetChecked, setLastCheckedIndex } = useContext(CheckedContext);
  const deliveryDict = useMiscStore((s) => s.deliveryDict);

  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const { pitchBP, setAndSendForBP } = useContext(AimingContext);

  const activeList = useListLibraryStore((s) => s.activeList);

  const isInGame = useAuthStore((s) => s.isInGame);
  const current = useAuthStore((s) => s.current);

  const { machine, getTrainingPitches, resetMSHash, setAutoFire } =
    useContext(MachineContext);

  const { safeSetIgnoreAf, setSuspended } = useContext(FiringContext);

  const dialog = usePitchListStore((s) => s.dialog);
  const filteredPitches = usePitchListStore((s) => s.filteredPitches);
  const loading = usePitchListStore((s) => s.loading);

  // whenever changing lists, clear the pitch selections in aiming context, pitch list store, etc...
  // reason: without this, the list and sidebar would render for a pitch that is not relevant anymore
  // e.g. w/ video selection for a pitch that isn't in the current list
  useEffect(() => {
    setAndSendForBP({
      pitch: undefined,
      resetPlate: ResetPlateMode.Skip,
      sendConfig: undefined,
    });
    usePitchListStore.getState().setSelectedPitch(undefined);
  }, [activeList?._id]);

  const [activeTab, setActiveTab] = useState<TabKey>(TabKey.Pitches);

  // auto-suspend the firing context whenever:
  // - a dialog is opened, or
  // - any tab other than pitches is active
  useEffect(() => {
    setSuspended(anyOpenDialogs || activeTab !== TabKey.Pitches);
  }, [anyOpenDialogs, activeTab]);

  const { setDialogSettings } = useContext(SessionEventsContext);

  // check if we need to close the training dialog because a game started
  useEffect(() => {
    if (!isInGame) {
      // game status does not block training
      return;
    }

    setActiveTab(TabKey.Pitches); // switch back to pitches tab if we're in a game

    if (dialog?.mode !== PitchListDialog.Training) {
      // training dialog is not open
      return;
    }

    // auto-end training
    NotifyHelper.warning({
      message_md: `Training is not allowed during home games.`,
    });

    onCloseTraining();

    usePitchListStore.getState().closeDialog();
  }, [
    isInGame, // if user was already training when a game starts (most likely)
    dialog, // if user tries to start training during a game (somehow)
  ]);

  const isAdmin = current.role === UserRole.admin;

  const onTrainFn: TrainPitchesFn = useCallback(
    async (pitchIDs: string[]) => {
      // always use pitches from the store in case they were (auto-)refreshed and differ from what's in selectedPitch or managePitches
      let trainingPitches = await getTrainingPitches(
        filteredPitches.filter((p) => pitchIDs.includes(p._id))
      );

      const breakPitches = trainingPitches.filter(
        (p) => p.priority === BuildPriority.Breaks
      );

      const hasSpinPitches = breakPitches.length < trainingPitches.length;
      const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
      const onTrackman = machine.tracking_device === TrackingDevice.TrackmanB1;

      const onBreakOnlyDevice = (onVision && !isAdmin) || onTrackman;

      // vision currently only supports Break priority pitches
      if (hasSpinPitches && onBreakOnlyDevice) {
        NotifyHelper.warning({
          message_md: onVision
            ? t('common.vision-training-no-spin')
            : t('common.trackman-training-no-spin'),
          buttons: [
            {
              label: 'common.open-settings',
              onClick: () => {
                usePitchListStore.getState().closeDialog(); // close training dialog and show settings dialog instead
                setDialogSettings({
                  identifier: 'BreakOnlyDeviceSettings',
                  initialSection: SettingsSection.Machine,
                  onClose: () => setDialogSettings(undefined),
                });
              },
              dismissAfterClick: true,
            },
          ],
        });

        // filter out all the Spin/Default priority pitches
        trainingPitches = breakPitches;
      }

      if (trainingPitches.length === 0) {
        // e.g. no ms values, cannot be built because of build priority vs what active model supports
        NotifyHelper.warning({
          message_md:
            'None of the selected pitches can be trained at this time.',
        });
        return;
      }

      // don't wait for rebuild to finish before opening the dialog
      usePitchListStore.getState().openDialog({
        dialog: PitchListDialog.Training,
        trainingPitches: trainingPitches,
      });

      // sidebar goes away, forces user to reselect a pitch after training
      setAndSendForBP({
        pitch: undefined,
        resetPlate: ResetPlateMode.Skip,
        sendConfig: undefined,
      });
    },
    [filteredPitches, getTrainingPitches, machine.tracking_device, isAdmin]
  );

  const onCloseTraining = useCallback(async () => {
    // trigger the listener to wait for machine to stop firing > lower the machine if necessary
    useTrainedMsStore.setState({
      lastTrainingEnded: Date.now(),
    });

    // user will need to S2M again
    resetMSHash();

    // deselect to force retrieval of shots on reselection
    usePitchListStore.getState().setSelectedPitch(undefined);

    // reload TMS for (possibly) trained pitches
    if (dialog?.trainingPitches) {
      const bsHashes = dialog.trainingPitches.map((p) => p.bs_hash) ?? [];

      if (bsHashes.length > 0) {
        useTrainedMsStore
          .getState()
          .buildTmsDict(
            machine.machineID,
            machine.ball_type,
            machine.last_hardware_changed,
            true
          );

        verboseDebug(props, 'reloaded TMS > trained pitches', {
          pitches: dialog.trainingPitches.map((p) => p.name),
        });
      }
    }

    usePitchListStore.getState().closeDialog();
  }, [
    dialog,
    resetMSHash,
    machine.machineID,
    machine.ball_type,
    machine.last_hardware_changed,
  ]);

  // when leaving a pitch list, automatically disable auto-fire and clear any leftover timeouts
  useEffect(() => {
    return () => {
      setAutoFire(false);
    };
  }, []);

  useEffect(() => {
    // switch back to Pitches tab if the user changes lists
    setActiveTab(TabKey.Pitches);
  }, [activeList?._id]);

  const onTabChange = (tab: TabKey) => {
    safeSetIgnoreAf(IgnoreAutoFireMode.Permanent);
    setActiveTab(tab);
  };

  // don't show pitches until we're sure the active list is set correctly
  const showSkeleton = loading || !props.ready;

  useEffect(() => {
    if (!props.ready) {
      return;
    }

    // make sure to clear checked statuses when switching between lists
    setLastCheckedIndex(null);
    resetChecked();
  }, [props.ready]);

  return (
    <ErrorBoundary identifier={props.identifier}>
      <Flex
        gap={RADIX.FLEX.GAP.SIDEBAR}
        justify="center"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          height: '100%',
        }}
      >
        {showSkeleton && (
          <Flex
            direction="column"
            gap="3"
            style={{
              height: '100%',
            }}
            flexGrow="1"
          >
            <PitchListSkeleton mode={props.mode} />
          </Flex>
        )}

        {/* main content */}
        {!showSkeleton && (
          <Flex
            direction="column"
            style={{ height: '100%' }}
            flexGrow="1"
            gap={props.mode === PLStoreMode.Library ? '3' : '1'}
          >
            {props.mode === PLStoreMode.Library ? (
              <PitchesHeader />
            ) : (
              <PitchListHeader onTrainFn={onTrainFn} />
            )}
            <PitchListTabs
              mode={props.mode}
              onTrainFn={onTrainFn}
              activeTab={activeTab}
              onTabChange={onTabChange}
            />
          </Flex>
        )}

        {/* sidebar */}
        <PitchListSidebar
          onVideoChanged={async (videoID) => {
            if (!pitchBP) {
              console.warn('Cannot change video without pitchBP');
              return;
            }

            const delivery = deliveryDict[pitchBP._id] ?? DEFAULT_DELIVERY;

            if (!delivery) {
              console.warn('Cannot change video without delivery');
              return;
            }

            switch (delivery) {
              case DeliveryType.Stretch: {
                if (pitchBP.stretchVideoID === videoID) {
                  return;
                }
                break;
              }

              case DeliveryType.Windup:
              default: {
                if (pitchBP.windupVideoID === videoID) {
                  return;
                }
                break;
              }
            }

            // check for video errors if necessary
            if (videoID) {
              const video = useVideosStore
                .getState()
                .videos.find((v) => v._id === videoID);

              if (!video) {
                NotifyHelper.error({
                  message_md: `Video \`${videoID}\` does not exist in context. Please try again.`,
                });
                return;
              }
            }

            const updatePayload: ForUpdate<
              IPitch & {
                // from old shape
                video_id: Nullable<string>;
              }
            > = { _id: pitchBP._id };

            // set the appropriate video prop based on delivery
            switch (delivery) {
              case DeliveryType.Stretch: {
                updatePayload.stretchVideoID = videoID;
                break;
              }

              case DeliveryType.Windup:
              default: {
                updatePayload.windupVideoID = videoID;
                break;
              }
            }

            if (!videoID && !isEmpty((pitchBP as any).video_id)) {
              // * removing a delivery-specific video should also null any old video_id value to allow suggestions
              updatePayload.video_id = null;
            }

            const updatedPitch = first(
              await usePitchListStore
                .getState()
                .updatePitches({ payloads: [updatePayload] })
            );

            if (!updatedPitch) {
              return;
            }

            await setAndSendForBP({
              pitch: updatedPitch,
              resetPlate: ResetPlateMode.Skip,
              sendConfig: undefined,
            });
          }}
          onTrainFn={onTrainFn}
          // keep the sidebar in the dom, but hide it if we're looking at hitting results
          className={activeTab === TabKey.Pitches ? undefined : 'hidden'}
          enableFire
          enableSaveAs
        />
      </Flex>

      {dialog?.mode === PitchListDialog.Training && dialog.trainingPitches && (
        <PresetTrainingDialogHoC
          key={dialog.key}
          identifier="PL-PT-TrainingDialog"
          pitches={dialog.trainingPitches}
          onClose={onCloseTraining}
        />
      )}

      <PitchListDialogs onTrainFn={onTrainFn} />
    </ErrorBoundary>
  );
};

interface IPitchListTabsProps {
  mode: PLStoreMode;
  onTrainFn: TrainPitchesFn;
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

enum TabKey {
  Pitches = 'Pitches',
  HittingResults = 'HittingResults',
}

enum DisplayMode {
  NoData,
  NoFilteredData,
  Default,
}

const PitchesTabContent = (props: {
  mode: PLStoreMode;
  onTrainFn: TrainPitchesFn;
}) => {
  const navigate = useNavigate();

  const activeList = useListLibraryStore((s) => s.activeList);

  const current = useAuthStore((s) => s.current);

  const pitches = usePitchListStore((s) => s.pitches);
  const filteredPitches = usePitchListStore((s) => s.filteredPitches);

  const displayMode = useMemo(() => {
    if (pitches.length === 0) {
      return DisplayMode.NoData;
    }

    if (filteredPitches.length === 0) {
      return DisplayMode.NoFilteredData;
    }

    return DisplayMode.Default;
  }, [pitches.length, filteredPitches.length]);

  // only hide the toolbar if the list is empty. We need to show it to undo filters if there are no results
  const showToolbar = displayMode !== DisplayMode.NoData;
  const showPlaceholder = [
    DisplayMode.NoData,
    DisplayMode.NoFilteredData,
  ].includes(displayMode);

  const playerPk = activeList?.mlb_repertoire?.playerPk;

  return (
    <ErrorBoundary identifier="PitchesTabContent">
      <Flex direction="column" height="100%" className={RADIX.VFLEX.WRAPPER}>
        <Flex
          direction="column"
          height="100%"
          flexShrink="1"
          minHeight="0"
          gap={showPlaceholder ? '0' : RADIX.FLEX.GAP.SM}
        >
          {showToolbar && <PitchListToolbar />}

          {displayMode === DisplayMode.NoData && (
            <NoDataPlaceholder
              noDataButton={
                current.mlb_stats_api ? (
                  <Box mt="2">
                    <Button
                      onClick={() => {
                        if (!playerPk) {
                          navigate(paths.pitches.gameData.root);
                          return;
                        }

                        navigate(
                          paths.pitches.gameData.pitcher(playerPk.toString())
                        );
                      }}
                    >
                      {t('pl.go-to-game-data')}
                    </Button>
                  </Box>
                ) : null
              }
              noDataHeader={t('pl.no-pitches-in-list').toString()}
              noDataBody={
                current.mlb_stats_api
                  ? t('pl.go-to-game-data-msg').toString()
                  : undefined
              }
            />
          )}

          {displayMode === DisplayMode.NoFilteredData && (
            <NoDataPlaceholder
              noDataButton={
                <Box mt="2">
                  <Button
                    onClick={() => usePitchListStore.getState().resetFilters()}
                  >
                    {t('hitters.reset-filters')}
                  </Button>
                </Box>
              }
              noDataBody={t('hitters.try-broader-search').toString()}
              className="height-100p"
            />
          )}

          {displayMode === DisplayMode.Default && (
            <>
              <PitchListBodyHoC onTrainFn={props.onTrainFn} />

              <Box pb="1">
                <PitchListFooter onTrainFn={props.onTrainFn} />
              </Box>
            </>
          )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

const PitchListTabs = (props: IPitchListTabsProps) => {
  const showNewAnalytics = useFeatureFlag(FF_NAME_ANALYTICS_2);

  const { onTrainFn } = props;

  const showRecap = usePitchListStore((s) => s.showRecap);
  const dialog = usePitchListStore((s) => s.dialog);

  const isInGame = useAuthStore((s) => s.isInGame);

  return (
    <ErrorBoundary identifier="PitchListTabs">
      <CommonTabs
        value={props.activeTab}
        onValueChange={(value) => props.onTabChange(value as TabKey)}
        gap="0"
        contentClassName="height-100p"
        className={RADIX.VFLEX.WRAPPER}
        tabs={[
          {
            value: TabKey.Pitches,
            label: 'common.pitches',
            content: (
              <PitchesTabContent mode={props.mode} onTrainFn={onTrainFn} />
            ),
          },
          {
            value: TabKey.HittingResults,
            label: 'common.results',
            disabled: !!isInGame,
            title: isInGame
              ? 'Hitting results are unavailable while a game is in progress'
              : undefined,
            content: showNewAnalytics ? (
              <PitchListHittingResultsNew />
            ) : (
              <PitchListHittingResults
                backToPitches={() => props.onTabChange(TabKey.Pitches)}
              />
            ),
          },
        ]}
      />

      {showRecap && (
        <Box display={props.activeTab === TabKey.Pitches ? 'block' : 'none'}>
          <PitchRecap
            // pause the recap widget while training
            paused={dialog?.mode === PitchListDialog.Training}
          />
        </Box>
      )}
    </ErrorBoundary>
  );
};
